import apiClient from '../../../shared/utils/axios';
import type {
  BrandFolder,
  CreativeResult,
  CreativeFilterDef,
  CreativeQueryParams,
} from '@/entities/creatives/types';

class CreativesAPI {
  async getCreativeFolderNames(): Promise<
    Array<Pick<BrandFolder, 'id' | 'name'>>
  > {
    const response = await apiClient.get<
      Array<Pick<BrandFolder, 'id' | 'name'>>
    >('/dooh_api/creative-library/v1/names');
    return response.data;
  }

  async getCreativeFolderById(id: string): Promise<BrandFolder> {
    const response = await apiClient.get<BrandFolder>(
      `/dooh_api/creative-library/v1/${id}`
    );
    return response.data;
  }

  async fetchCreatives(params: CreativeQueryParams): Promise<CreativeResult> {
    const { page = 0, rowsPerPage = 10, search = '' } = params || {};

    try {
      // Build URL with query parameters for backend pagination
      const limit = rowsPerPage;
      const offset = page * rowsPerPage;
      let url = `/dooh_api/creative-library/v1/list?limit=${limit}&offset=${offset}`;

      if (search && search.length > 2) {
        url += `&searchText=${encodeURIComponent(search)}`;
      }

      const response = await apiClient.post<
        Pick<CreativeResult, 'headers' | 'rows'> & { total: number }
      >(url, {
        filters: [
          {
            key: 'deleted',
            type: 'terms',
            value: [false],
          },
        ],
      });

      const data = response?.data || { rows: [], total: 0, headers: [] };
      return {
        headers: data.headers || [],
        rows: data.rows || [],
        totalItems: data.total || 0,
        page,
        rowsPerPage,
      };
    } catch (error) {
      console.error('Error fetching creatives:', error);
      throw error;
    }
  }

  async fetchCreativeFilters(): Promise<CreativeFilterDef[]> {
    try {
      const response = await apiClient.get<CreativeFilterDef[]>(
        `/dooh_api/creative-library/v1/filters`
      );
      return response?.data || [];
    } catch (error) {
      console.error('Error fetching creative filters:', error);
      return [];
    }
  }

  async uploadCreative(data: any): Promise<any> {
    try {
      const response = await apiClient.post(
        `/dooh_api/creative-library/v1/upload`,
        data
      );
      return response;
    } catch (error) {
      console.error('Error uploading creative:', error);
      throw error;
    }
  }

  async deleteCreative(id: string | number): Promise<any> {
    try {
      const response = await apiClient.delete(
        `/dooh_api/creative-library/v1/${id}`
      );
      return response;
    } catch (error) {
      console.error('Error deleting creative:', error);
      throw error;
    }
  }

  async editCreativeAsset(
    brandId: string,
    body: { action: 'ADD' | 'DELETE'; assetId: string[] | null; data: any }
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `/dooh_api/creative-library/v1/edit/${brandId}`,
        body,
        { timeout: 600000 }
      );
      return response;
    } catch (error) {
      console.error('Error editing creative asset:', error);
      throw error;
    }
  }

  async getScreenPlaylist(deviceId: string | number): Promise<any> {
    try {
      const response = await apiClient.get(
        `/dooh_api/playlist/v1/device/${deviceId}/raw`
      );
      return response;
    } catch (error) {
      console.error('Error fetching screen playlist:', error);
      throw error;
    }
  }

  async createScreenPlaylist(payload: {
    selectedCreatives: any[];
    deviceId: string | number;
  }): Promise<any> {
    let loopDurationSec = 0;

    const slots = payload.selectedCreatives?.map((item, index) => {
      loopDurationSec += parseInt(item.duration || 0);
      const { id, src, description, resolution, duration, name } = item;
      const [width, height] = resolution?.split('x') || [0, 0];

      return {
        index,
        items: [
          {
            id,
            type: src?.endsWith('.mp4') ? 'VIDEO' : 'IMAGE',
            description,
            resolution: {
              width: parseInt(width),
              height: parseInt(height),
            },
            creative: {
              creativeId: id,
              src,
            },
            duration,
            name,
          },
        ],
      };
    });

    const body = {
      name: 'Main Playlist',
      targetType: 'DEVICE',
      targetId: payload.deviceId,
      loopDurationSec,
      slots,
      fallback: { items: [] },
    };

    try {
      const response = await apiClient.post(`/dooh_api/playlist/v1`, body);
      return response;
    } catch (error) {
      console.error('Error creating screen playlist:', error);
      throw error;
    }
  }

  async updateScreenPlaylist(payload: {
    selectedCreatives: any[];
    playlistId: string;
    deviceId: string | number;
  }): Promise<any> {
    const getResolution = (resolution: any) => {
      if (typeof resolution === 'string') {
        const [width, height] = resolution
          .split('x')
          .map((item: string) => parseInt(item));
        return {
          width,
          height,
        };
      }
      return resolution;
    };

    let loopDurationSec = 0;

    const slots = payload.selectedCreatives.map((item, index) => {
      loopDurationSec += parseInt(item.duration || 0);
      const {
        id,
        creative,
        description,
        resolution,
        duration,
        name,
        type,
        src,
      } = item;
      return {
        index,
        items: [
          {
            id,
            type: type || (src?.endsWith('.mp4') ? 'VIDEO' : 'IMAGE'),
            description,
            resolution: getResolution(resolution),
            creative: creative || {
              creativeId: id,
              src,
            },
            duration,
            name,
          },
        ],
      };
    });

    const body = {
      loopDurationSec,
      slots,
    };

    try {
      const response = await apiClient.put(
        `/dooh_api/playlist/v1/${payload.playlistId}`,
        body
      );
      return response;
    } catch (error) {
      console.error('Error updating screen playlist:', error);
      throw error;
    }
  }

  async updatePlaylistCreatives(
    deviceId: string | number,
    body: {
      creatives: Array<{
        id: string;
        slotIndex: number;
        priority: number;
        campaignId: string;
        creativeAssetId: string;
      }>;
    }
  ): Promise<any> {
    try {
      const response = await apiClient.put(
        `/dooh_api/playlist/v1/device/${deviceId}/priority`,
        body
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export const creativesAPI = new CreativesAPI();
export default creativesAPI;
